import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { calculateAge, gestationalAgeFor } from "./dates";
import {
  ageBandForAge,
  bmiCategoryFor,
  calculateBmi,
  calorieTarget,
  estimateNonPregnantEer,
  macroTargets,
  MICRONUTRIENT_TARGETS,
  weightGainBandFor,
} from "./nutritionEngine";
import type { Profile } from "@/types";

export function useNutritionTargets(profile: Profile | null) {
  const [trimesterToast, setTrimesterToast] = useState<string | null>(null);

  const computed = useMemo(() => {
    if (!profile) return null;
    const age = calculateAge(profile.dateOfBirth);
    const { week, trimester } = gestationalAgeFor(profile.lmpDate);
    const ageBand = ageBandForAge(age);
    const nonPregnantEer = estimateNonPregnantEer({
      weightKg: profile.prePregnancyWeightKg,
      heightCm: profile.heightCm,
      ageYears: age,
      activityLevel: profile.activityLevel,
    });
    const calories = calorieTarget({
      nonPregnantEerKcal: nonPregnantEer,
      trimester,
      fetusCount: profile.fetusCount,
    });
    const macros = macroTargets(calories);
    const micronutrients = MICRONUTRIENT_TARGETS[ageBand];
    const bmi = calculateBmi(profile.prePregnancyWeightKg, profile.heightCm);
    const bmiCategory = bmiCategoryFor(bmi);
    const weightGainBand = weightGainBandFor({ bmiCategory, fetusCount: profile.fetusCount });

    return {
      gestationalWeek: week,
      trimester,
      ageBand,
      calories,
      macros,
      micronutrients,
      bmiCategory,
      weightGainBand,
    };
  }, [profile]);

  // One-time toast when the user crosses into a new trimester.
  // profiles.lastNotifiedTrimester is the source of truth so this survives
  // across sessions and devices, not just this browser.
  useEffect(() => {
    if (!profile || !computed) return;
    if (profile.lastNotifiedTrimester !== computed.trimester) {
      setTrimesterToast(
        `You've moved into trimester ${computed.trimester} — your nutrition targets just updated.`
      );
      api.profile.update({ lastNotifiedTrimester: computed.trimester } as never).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.userId, computed?.trimester]);

  return { ...computed, trimesterToast, dismissTrimesterToast: () => setTrimesterToast(null) };
}
